import "@/App.css";
import "@/styles/design-tokens.css";
import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
import DashboardLayout from "@/components/DashboardLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
const ProductCatalogue = lazy(() => import("@/pages/ProductCatalogue"));
const ProductRangePage = lazy(() => import("@/pages/ProductRangePage"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const NigeriaDispatchHistoryPage = lazy(() => import("@/pages/NigeriaDispatchHistoryPage"));
const ProfitCalculator = lazy(() => import("@/pages/ProfitCalculator"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AgentDashboard = lazy(() => import("@/pages/AgentDashboard"));
const AgentCommissionsPage = lazy(() => import("@/pages/AgentCommissionsPage"));
const SalesRepDashboard = lazy(() => import("@/pages/SalesRepDashboard"));
const NickyFinanceDashboard = lazy(() => import("@/pages/NickyFinanceDashboard"));
const SalonDashboard = lazy(() => import("@/pages/SalonDashboard"));
const StarterPacksPage = lazy(() => import("@/pages/StarterPacksPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const DistributorPacksPage = lazy(() => import("@/pages/DistributorPacksPage"));
const DistributorPackDetailPage = lazy(() => import("@/pages/DistributorPackDetailPage"));
const DistributorDashboard = lazy(() => import("@/pages/DistributorDashboard"));
const VisitTrackingPage = lazy(() => import("@/pages/VisitTrackingPage"));


const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const TeamMemberDashboard = lazy(() => import("@/pages/TeamMemberDashboard"));
const RewardsPage = lazy(() => import("@/pages/RewardsPage"));
const RevenueTracker = lazy(() => import("@/pages/RevenueTracker"));
const ProStatusLadder = lazy(() => import("@/pages/ProStatusLadder"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const FindSalonPage = lazy(() => import("@/pages/FindSalonPage"));
const SalonProfilePage = lazy(() => import("@/pages/SalonProfilePage"));
const DashboardSalonProfile = lazy(() => import("@/pages/DashboardSalonProfile"));
const StaffOnboarding = lazy(() => import("@/pages/StaffOnboarding"));
const CompleteProfilePage = lazy(() => import("@/pages/CompleteProfilePage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AcademyLayout = lazy(() => import("@/pages/academy/AcademyLayout"));
const LearningPage = lazy(() => import("@/pages/LearningPage"));
const AcademyHome = lazy(() => import("@/pages/academy/AcademyHome"));
const AcademyWelcome = lazy(() => import("@/pages/academy/AcademyWelcome"));
const AcademyHairTextures = lazy(() => import("@/pages/academy/AcademyHairTextures"));
const AcademyCourses = lazy(() => import("@/pages/academy/AcademyCourses"));
const AcademyProducts = lazy(() => import("@/pages/academy/AcademyProducts"));
const AcademyProduct = lazy(() => import("@/pages/academy/AcademyProduct"));
const ClientShopNG = lazy(() => import("@/pages/ClientShopNG"));
import EducatorHome, { EducatorCalendar, EducatorLive, EducatorAsk, EducatorRequests, EducatorStudents } from "@/pages/EducatorSuite";
const RepQuickOrder = lazy(() => import("@/pages/RepSuite").then(m => ({ default: m.RepQuickOrder })));
const RepSalons = lazy(() => import("@/pages/RepSuite").then(m => ({ default: m.RepSalons })));
const RepOrders = lazy(() => import("@/pages/RepSuite").then(m => ({ default: m.RepOrders })));
const RepLeads = lazy(() => import("@/pages/RepSuite").then(m => ({ default: m.RepLeads })));
const RepCommission = lazy(() => import("@/pages/RepSuite").then(m => ({ default: m.RepCommission })));
const AgentClients = lazy(() => import("@/pages/AgentSuite").then(m => ({ default: m.AgentClients })));
const AgentNewOrder = lazy(() => import("@/pages/AgentSuite").then(m => ({ default: m.AgentNewOrder })));
const AgentCommission = lazy(() => import("@/pages/AgentSuite").then(m => ({ default: m.AgentCommission })));
const AgentOrders = lazy(() => import("@/pages/AgentSuite").then(m => ({ default: m.AgentOrders })));
const DistPlaceOrder = lazy(() => import("@/pages/DistributorSuite").then(m => ({ default: m.DistPlaceOrder })));
const DistOrders = lazy(() => import("@/pages/DistributorSuite").then(m => ({ default: m.DistOrders })));
const DistInvoices = lazy(() => import("@/pages/DistributorSuite").then(m => ({ default: m.DistInvoices })));
const DistSalons = lazy(() => import("@/pages/DistributorSuite").then(m => ({ default: m.DistSalons })));
const AcademySales = lazy(() => import("@/pages/academy/AcademySales"));
const AcademySetup = lazy(() => import("@/pages/academy/AcademySetup"));
const AcademyPreview = lazy(() => import("@/pages/academy/AcademyPreview"));
const AcademyStub = lazy(() => import("@/pages/academy/AcademyStub"));
const AcademyEducation = lazy(() => import("@/pages/academy/AcademyEducation"));
const AcademyLesson = lazy(() => import("@/pages/academy/AcademyLesson"));
const AcademyMethod = lazy(() => import("@/pages/academy/AcademyMethod"));
const AcademyTest = lazy(() => import("@/pages/academy/AcademyTest"));
const AcademyCertificate = lazy(() => import("@/pages/academy/AcademyCertificate"));
const VerifyCertificate = lazy(() => import("@/pages/VerifyCertificate"));
const AcademyLive = lazy(() => import("@/pages/academy/AcademyLive"));
const ClassManage = lazy(() => import("@/pages/academy/ClassManage"));
const ClassRoom = lazy(() => import("@/pages/academy/ClassRoom"));
const ClassPresent = lazy(() => import("@/pages/academy/ClassPresent"));
const ClassConsole = lazy(() => import("@/pages/academy/ClassConsole"));
const JoinClass = lazy(() => import("@/pages/JoinClass"));
const AcademyFinder = lazy(() => import("@/pages/academy/AcademyFinder"));
const AcademyCompare = lazy(() => import("@/pages/academy/AcademyCompare"));
const AcademyMyLearning = lazy(() => import("@/pages/academy/AcademyMyLearning"));
const AcademyRankings = lazy(() => import("@/pages/academy/AcademyRankings"));
const AcademyCeo = lazy(() => import("@/pages/academy/AcademyCeo"));
const AcademyQuestionBank = lazy(() => import("@/pages/academy/AcademyQuestionBank"));
// Revitalise Salon Booking & Business Platform (booking_platform_build_01nov2026_go)
const BookingLayout = lazy(() => import("@/pages/booking/BookingLayout"));
const BookingEmbedPage = lazy(() => import("@/pages/BookingEmbedPage"));
const CatchupEmbedPage = lazy(() => import("@/pages/CatchupEmbedPage"));
const PublicCatchupEmbed = lazy(() => import("@/pages/PublicCatchupEmbed"));
const BookingHome = lazy(() => import("@/pages/booking/BookingHome"));
const BookingCalendar = lazy(() => import("@/pages/booking/BookingCalendar"));
const BookingClients = lazy(() => import("@/pages/booking/BookingClients"));
const BookingCheckout = lazy(() => import("@/pages/booking/BookingCheckout"));
const BookingTeam = lazy(() => import("@/pages/booking/BookingTeam"));
const BookingStock = lazy(() => import("@/pages/booking/BookingStock"));
const BookingPlans = lazy(() => import("@/pages/booking/BookingPlans"));
const BookingAI = lazy(() => import("@/pages/booking/BookingAI"));
const BookingReports = lazy(() => import("@/pages/booking/BookingReports"));
const BookingSettings = lazy(() => import("@/pages/booking/BookingSettings"));
const BookingNetwork = lazy(() => import("@/pages/booking/BookingNetwork"));
const SalonFinderPage = lazy(() => import("@/pages/booking/SalonFinderPage"));
const BookOnlinePage = lazy(() => import("@/pages/booking/BookOnlinePage"));
const DoorCheckInPage = lazy(() => import("@/pages/booking/DoorCheckInPage"));
const BkPayResult = lazy(() => import("@/pages/booking/BkPayResult"));
const RegisterFormPage = lazy(() => import("@/pages/booking/RegisterFormPage"));
const BookingScan = lazy(() => import("@/pages/booking/BookingScan"));
const BookingJuniorTraining = lazy(() => import("@/pages/booking/BookingJuniorTraining"));
const ByMarinaShopPage = lazy(() => import("@/pages/ByMarinaShopPage"));
const AcademyScience = lazy(() => import("@/pages/academy/AcademyScience"));
const AcademyRefPage = lazy(() => import("@/pages/academy/AcademyRefPage"));
const AcademyNotes = lazy(() => import("@/pages/academy/AcademyNotes"));
const AcademyIdeas = lazy(() => import("@/pages/academy/AcademyIdeas"));
const AcademyInsights = lazy(() => import("@/pages/academy/AcademyInsights"));
const JournalPage = lazy(() => import("@/pages/journal/JournalPage"));
const JournalStoryPage = lazy(() => import("@/pages/journal/JournalStoryPage"));
const JournalEditor = lazy(() => import("@/pages/journal/JournalEditor"));
const BookingProfile = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingProfile })));
const BookingWaitlist = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingWaitlist })));
const BookingReviewsPoints = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingReviewsPoints })));
const BookingUseWaste = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingUseWaste })));
const BookingReorder = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingReorder })));
const BookingVouchers = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingVouchers })));
const BkPaymentsPage = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingPayments })));
const BookingNoticeboard = lazy(() => import("@/pages/booking/BookingExtraPages").then(m => ({ default: m.BookingNoticeboard })));
const BookingMarketing = lazy(() => import("@/pages/booking/BookingExtraPages2").then(m => ({ default: m.BookingMarketing })));
const BookingEducation = lazy(() => import("@/pages/booking/BookingExtraPages2").then(m => ({ default: m.BookingEducation })));
const BookingJuniors = lazy(() => import("@/pages/booking/BookingExtraPages2").then(m => ({ default: m.BookingJuniors })));
const BookingHelp = lazy(() => import("@/pages/booking/BookingExtraPages2").then(m => ({ default: m.BookingHelp })));
const BookingServicesAdmin = lazy(() => import("@/pages/booking/BookingExtraPages2").then(m => ({ default: m.BookingServicesAdmin })));
const BookingOnlineLinks = lazy(() => import("@/pages/booking/BookingExtraPages2").then(m => ({ default: m.BookingOnlineLinks })));
const BookingFormsAdmin = lazy(() => import("@/pages/booking/BookingExtraPages2").then(m => ({ default: m.BookingFormsAdmin })));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));


const SalonBusinessPlanner = lazy(() => import("@/pages/SalonBusinessPlanner"));
const ReorderSuggestions = lazy(() => import("@/pages/ReorderSuggestions"));
const SecuritySettings = lazy(() => import("@/pages/SecuritySettings"));
const ThankYouAdmin = lazy(() => import("@/pages/ThankYouAdmin"));
const ProspectCRM = lazy(() => import("@/pages/ProspectCRM"));
const DemandForecast = lazy(() => import("@/pages/DemandForecast"));
const TerritoryPerformance = lazy(() => import("@/pages/TerritoryPerformance"));
const WarehouseReorder = lazy(() => import("@/pages/WarehouseReorder"));
const MessagingSystem = lazy(() => import("@/pages/MessagingSystem").then(m => ({ default: m.NotificationInbox })));
const WhatsAppBriefing = lazy(() => import("@/pages/WhatsAppBriefing"));
const VerificationQueue = lazy(() => import("@/pages/VerificationQueue"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionsDashboard"));
const VerificationHub = lazy(() => import("@/pages/VerificationHub"));
const PayrollDashboard = lazy(() => import("@/pages/PayrollDashboard"));
const MyPayroll = lazy(() => import("@/pages/MyPayroll"));
const WeeklyDigest = lazy(() => import("@/pages/WeeklyDigest"));
const SalesFunnel = lazy(() => import("@/pages/SalesFunnel"));
const StockManagement = lazy(() => import("@/pages/StockManagement"));
const StockReconciliation = lazy(() => import("@/pages/StockReconciliation"));
const BarcodeScanner = lazy(() => import("@/pages/BarcodeScanner"));
const ProductScannerPage = lazy(() => import("@/pages/ProductScannerPage"));
const SalonSettings = lazy(() => import("@/pages/SalonSettings"));
const MyProfile = lazy(() => import("@/pages/MyProfile"));
const SalonRegistryPage = lazy(() => import("@/pages/SalonRegistryPage"));
const SalonPerformanceDashboard = lazy(() => import("@/pages/SalonPerformanceDashboard"));
const CEOCommandPage = lazy(() => import("@/pages/CEOCommandPage"));
const WelcomeCampaignPage = lazy(() => import("@/pages/WelcomeCampaignPage"));
const RunMigrationPage = lazy(() => import("@/pages/RunMigrationPage"));
const CEOEmailLogPage = lazy(() => import("@/pages/CEOEmailLogPage"));
const SalesLogPage = lazy(() => import("@/pages/SalesLogPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const DeliveryDashboard = lazy(() => import("@/pages/DeliveryDashboard"));
const Lookbook = lazy(() => import("@/pages/Lookbook"));
const ModerationDashboard = lazy(() => import("@/pages/ModerationDashboard"));
const MediaReviewPage = lazy(() => import("@/pages/MediaReviewPage"));
const SalesAnalytics = lazy(() => import("@/pages/SalesAnalytics"));
const RepDetail = lazy(() => import("@/pages/RepDetail"));
const MediaEditor = lazy(() => import("@/pages/MediaEditor"));
const RepSharePack = lazy(() => import("@/pages/RepSharePack"));
const LeadCapture = lazy(() => import("@/pages/LeadCapture"));
const LeadsDashboard = lazy(() => import("@/pages/LeadsDashboard"));
const FinishProduct = lazy(() => import("@/pages/FinishProduct"));
const InventoryIntelligence = lazy(() => import("@/pages/InventoryIntelligence"));
const FeatureToggles = lazy(() => import("@/pages/FeatureToggles"));
const BulletinBoard = lazy(() => import("@/pages/BulletinBoard"));
const RepAlerts = lazy(() => import("@/pages/RepAlerts"));
const StockSetup = lazy(() => import("@/pages/StockSetup"));
const SalonDirectory = lazy(() => import("@/pages/SalonDirectory"));
const PublicRegisterPage = lazy(() => import("@/pages/PublicRegisterPage"));
const StaffDirectory = lazy(() => import("@/pages/StaffDirectory"));
const HomepageEditor = lazy(() => import("@/pages/HomepageEditor"));
const UKWarehouse = lazy(() => import("@/pages/UKWarehouse"));
const UKDispatchDashboard = lazy(() => import("@/pages/UKDispatchDashboard"));
const QuickOrderPage = lazy(() => import("@/pages/QuickOrderPage"));
const ReplacementCreatePage = lazy(() => import("@/pages/ReplacementCreatePage"));
const MySalonInvoicesPage = lazy(() => import("@/pages/MySalonInvoicesPage"));
const MySalonOrdersPage = lazy(() => import("@/pages/MySalonOrdersPage"));
const MySalonQuotesPage = lazy(() => import("@/pages/MySalonQuotesPage"));
const ManualStockCheckPage = lazy(() => import("@/pages/ManualStockCheckPage"));
const FactoryOrders = lazy(() => import("@/pages/FactoryOrders"));
const CostDashboard = lazy(() => import("@/pages/CostDashboard"));
const FinanceDashboard = lazy(() => import("@/pages/FinanceDashboard"));
const UKDistributors = lazy(() => import("@/pages/UKDistributors"));
const KeratinKitsPage = lazy(() => import("@/pages/KeratinKitsPage"));
const UKDistributorDashboard = lazy(() => import("@/pages/UKDistributorDashboard"));
const KenyaMyProductsPage = lazy(() => import("@/pages/KenyaMyProductsPage"));
const KenyaOrderTracker = lazy(() => import("@/pages/KenyaOrderTracker"));
const CampaignImagesPage = lazy(() => import("@/pages/CampaignImagesPage"));
const UKOrdersPage = lazy(() => import("@/pages/UKOrdersPage"));
const UKOrderDispatchPage = lazy(() => import("@/pages/UKOrderDispatchPage"));
const ReportedIssuesPage = lazy(() => import("@/pages/ReportedIssuesPage"));
const OrderQueuePage = lazy(() => import("@/pages/OrderQueuePage"));
const WarehouseDashboard = lazy(() => import("@/pages/WarehouseDashboard"));
const DispatchHistoryPage = lazy(() => import("@/pages/DispatchHistoryPage"));
const UKInvoicesPage = lazy(() => import("@/pages/UKInvoicesPage"));
const NigeriaInvoicesPage = lazy(() => import("@/pages/NigeriaInvoicesPage"));
const MySalesRepInvoicesPage = lazy(() => import("@/pages/MySalesRepInvoicesPage"));
const MasterDashboardPage = lazy(() => import("@/pages/MasterDashboardPage"));
const UKQuotesPage = lazy(() => import("@/pages/UKQuotesPage"));
const DistributorOnboardingPage = lazy(() => import("@/pages/DistributorOnboardingPage"));
const SalonOnboardingPage = lazy(() => import("@/pages/SalonOnboardingPage"));
const NigeriaSalonQueuePage = lazy(() => import("@/pages/NigeriaSalonQueuePage"));
const UnifiedOnboardingPage = lazy(() => import("@/pages/UnifiedOnboardingPage"));
const DemoLandingPage = lazy(() => import("@/pages/DemoLandingPage"));
const DemoInvitePage = lazy(() => import("@/pages/DemoInvitePage"));
const StartHerePage = lazy(() => import("@/pages/StartHerePage"));
import DemoBanner from "@/components/DemoBanner";
import DemoConversionBanner from "@/components/DemoConversionBanner";
import GuidedTour from "@/components/GuidedTour";
const SalonPipelinePage = lazy(() => import("@/pages/SalonPipelinePage"));
const NgPaymentAccountsPage = lazy(() => import("@/pages/NgPaymentAccountsPage"));
const NgUsdPricingPage = lazy(() => import("@/pages/NgUsdPricingPage"));
const SalonPreviewPage = lazy(() => import("@/pages/SalonPreviewPage"));
const SalonJoinPage = lazy(() => import("@/pages/SalonJoinPage"));
const DistributorJoinPage = lazy(() => import("@/pages/DistributorJoinPage"));
const SalonCRMDetailPage = lazy(() => import("@/pages/SalonCRMDetailPage"));
const StockTransferPage = lazy(() => import("@/pages/StockTransferPage"));
const GlobalSalonDashboard = lazy(() => import("@/pages/GlobalSalonDashboard"));
const SalonHome27oct = lazy(() => import("@/pages/SalonHome27oct"));
const UKSalonsAdminPage = lazy(() => import("@/pages/UKSalonsAdminPage"));
const AutomationDashboard = lazy(() => import("@/pages/AutomationDashboard"));
const CEODigestPage = lazy(() => import("@/pages/CEODigestPage"));
const WhatsAppAutomationPage = lazy(() => import("@/pages/WhatsAppAutomationPage"));
const WhatsAppInbox = lazy(() => import("@/pages/WhatsAppInbox"));

const ResultLogbookPage = lazy(() => import("@/pages/ResultLogbookPage"));
const SalonSearchPage = lazy(() => import("@/pages/SalonSearchPage"));
const StaffInviteAcceptPage = lazy(() => import("@/pages/StaffInviteAcceptPage"));
const ResultDetailPage = lazy(() => import("@/pages/ResultDetailPage"));
const ResultsGalleryPage = lazy(() => import("@/pages/ResultsGalleryPage"));
const BeforeAfterPage = lazy(() => import("@/pages/BeforeAfterPage"));
const ReorderIntelligencePage = lazy(() => import("@/pages/ReorderIntelligencePage"));
const CommandCentrePage = lazy(() => import("@/pages/CommandCentrePage"));
const StarterPackDetailPage = lazy(() => import("@/pages/StarterPackDetailPage"));
const ProductControlPage = lazy(() => import("@/pages/ProductControlPage"));
const CampaignControlPage = lazy(() => import("@/pages/CampaignControlPage"));
const WelcomePage = lazy(() => import("@/pages/WelcomePage"));
const UKSalonRegisterPage = lazy(() => import("@/pages/UKSalonRegisterPage"));
const UniversalEntryPage = lazy(() => import("@/pages/UniversalEntryPage"));
// ceo_instruction_ref: universal_entry_country_landing_build_19aug2026
const CountryLandingPage = lazy(() => import("@/pages/CountryLandingPage"));
const MyTeamPage = lazy(() => import("@/pages/MyTeamPage"));
const CEOTeamOverview = lazy(() => import("@/pages/CEOTeamOverview"));
const CEODiscountControl = lazy(() => import("@/pages/CEODiscountControl"));
import RegionSelector from "@/components/RegionSelector";
const RepStockCheckPage = lazy(() => import("@/pages/RepStockCheckPage"));
const TransferRequestsPage = lazy(() => import("@/pages/TransferRequestsPage"));
const CEOStockRepsPage = lazy(() => import("@/pages/CEOStockRepsPage"));
const ProductRequestsPage = lazy(() => import("@/pages/ProductRequestsPage"));
const SecurityLogPage = lazy(() => import("@/pages/SecurityLogPage"));
// CEOWarehouseOverview RETIRED (ceo_exposure_counts_14sep2026) — its
// route now lands on the live WarehouseDashboard.
const ShareViewPage = lazy(() => import("@/pages/ShareViewPage"));
const PendingApprovalPage = lazy(() => import("@/pages/PendingApprovalPage"));
const VisitorDemoPage = lazy(() => import("@/pages/VisitorDemoPage"));
const SetPasswordPage = lazy(() => import("@/pages/SetPasswordPage"));
const TeamPerformancePage = lazy(() => import("@/pages/TeamPerformancePage"));
const SuperRepDashboard = lazy(() => import("@/pages/SuperRepDashboard"));
const NewsletterGenerator = lazy(() => import("@/pages/NewsletterGenerator"));
const PaymentReviewDashboard = lazy(() => import("@/pages/PaymentReviewDashboard"));
const MyReceiptsPage = lazy(() => import("@/pages/MyReceiptsPage"));
const ReceiptUploadGuide = lazy(() => import("@/pages/ReceiptUploadGuide"));
const QuoteAcceptPage = lazy(() => import("@/pages/QuoteAcceptPage"));
const QuoteChangeRequestPage = lazy(() => import("@/pages/QuoteChangeRequestPage"));

const MULTI_REGION_ROLES = ['founder', 'admin', 'super_admin', 'admin_manager'];

const RegionGate = ({ children }) => {
  const { user } = useAuth();
  const [regionChosen, setRegionChosen] = useState(false);

  // Show region selector for multi-region users who haven't selected yet
  if (user && MULTI_REGION_ROLES.includes(user.role) && !user.selected_region && !regionChosen) {
    return <RegionSelector onSelect={() => setRegionChosen(true)} />;
  }
  return children;
};

/**
 * Root route (/) handler — authed users get sent to their dashboard,
 * unauthed users see the country-selection entry page.
 *
 * Prevents Safari back-button bouncing logged-in users onto the flag
 * picker via the catch-all `*` route. (Bug report 18-Apr-2026.)
 */
const RootEntry = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <UniversalEntryPage />;
};

/**
 * Catch-all route handler — if user is authed, send them to their
 * dashboard; if not, to login. Never to the flag-picker.
 */
const NotFoundRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) {
    // ceo_email_links_07oct2026: carry the attempted path into login so an
    // email deep link opened while logged out still lands on its record
    // after sign-in — never the home page.
    const target = `${location.pathname}${location.search}`;
    const suffix = target && target !== '/' ? `?redirect=${encodeURIComponent(target)}` : '';
    return <Navigate to={`/login${suffix}`} replace />;
  }
  // ceo_instruction_ref: not_found_named_path_17sep2026 — never silently
  // bounce an authed user to the dashboard: name the path that failed so a
  // broken link is discovered on first tap, not a month later.
  const failedPath = `${location.pathname}${location.search}`;
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] px-6" data-testid="not-found-page">
      <div className="max-w-lg w-full border border-amber-300 bg-amber-50 rounded-md p-6 text-center">
        <p className="text-[10px] uppercase tracking-[2px] text-amber-700 font-semibold mb-2">Page not found</p>
        <p className="text-sm text-[#2B1F27] mb-1">This link points to a path that does not exist:</p>
        <p className="text-sm font-mono font-semibold text-amber-800 break-all mb-4" data-testid="not-found-path">{failedPath}</p>
        <p className="text-xs text-[#64525D] mb-4">Nothing was opened. If a notification or email brought you here, the destination it carries is wrong — report the path above.</p>
        <a href="/dashboard" className="inline-block bg-[#2B1F27] text-white text-xs font-semibold px-5 py-2.5 rounded" data-testid="not-found-dashboard-link">Go to Dashboard</a>
      </div>
    </div>
  );
};

const PendingGate = ({ children }) => {
  const { user } = useAuth();
  const adminRoles = ['founder', 'admin', 'super_admin', 'admin_manager', 'warehouse_admin', 'demo_user', 'sales_rep', 'agent', 'delivery_agent'];
  if (user && !adminRoles.includes(user.role) && user.verified === false && user.verification_status === 'pending') {
    return <PendingApprovalPage />;
  }
  return children;
};

/**
 * ──────────────────────────────────────────────────────────────
 * SalonIntelGate — RBAC + region + territory route gate
 * ceo_instruction_ref:
 *   rbac_region_lockdown_step3_04may2026
 *
 * Step 3 evolution of the Step 1 interim block. Default
 * allowedRoles preserve the Step 1 lockdown (founder + sales_rep
 * only) for the Salon Registry / Pipeline / Sales Funnel /
 * Lead Pipeline / Demo Invites / Leads / Salon Directory group.
 *
 * Props:
 *   - children          (required)
 *   - allowedRoles      Array of role strings allowed through the
 *                       gate. Default: ['founder', 'ceo',
 *                       'sales_rep'] (Step 1 default).
 *   - allowOwnerSelf    If true, salon-owner-class roles are also
 *                       allowed; if `ownerIdParam` is also
 *                       provided, the URL :userId/:salonId is
 *                       compared to user.user_id and only matches
 *                       are admitted.
 *   - ownerIdParam      String name of the URL param to compare
 *                       against user.user_id (e.g. "userId",
 *                       "salonId"). Required when the page is
 *                       URL-scoped to a specific owner.
 * ──────────────────────────────────────────────────────────────
 */
const SALON_INTEL_DEFAULT_ROLES = ['founder', 'ceo', 'sales_rep', 'agent'];
const SALON_OWNER_CLASS_ROLES = [
  'salon_owner', 'salon_staff', 'salon',
  'salon_business', 'salon_manager', 'professional',
  'team_member', // team_invoices_27oct2026 — ticked team members reach salon invoices/quotes
];

const SalonIntelGate = ({ children, allowedRoles, allowOwnerSelf, ownerIdParam }) => {
  const { user, loading } = useAuth();
  const params = useParams();
  if (loading) return null;
  const role = (user?.role || '').toLowerCase();
  const roles = allowedRoles || SALON_INTEL_DEFAULT_ROLES;

  if (roles.includes(role)) {
    return children;
  }

  if (allowOwnerSelf && SALON_OWNER_CLASS_ROLES.includes(role)) {
    if (!ownerIdParam) {
      // No URL param to compare → trust backend self-scoping
      // (e.g. /my-salon-orders, /salon-settings).
      return children;
    }
    const urlId = params[ownerIdParam];
    if (urlId && user?.user_id && urlId === user.user_id) {
      return children;
    }
  }

  return <Navigate to="/dashboard" replace />;
};

// ceo_instruction_ref: demo_user_read_lockdown_09aug2026 — route gate for
// CEO/finance surfaces. Denies demo_user and any non-allowlisted role.
const UK_COMMERCIAL_ROLES = ['founder', 'ceo', 'super_admin', 'admin', 'admin_manager', 'finance_director'];

// Role-aware /dashboard/invoices redirect: admins see the UK invoice book,
// salons see their own invoices (invoices_redirect_role_aware_23oct2026).
const InvoicesRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const to = UK_COMMERCIAL_ROLES.includes((user?.role || '').toLowerCase())
    ? '/dashboard/uk-invoices'
    : '/dashboard/my-salon-invoices';
  return <Navigate to={to} replace />;
};
const FINANCE_DIRECTOR_ROLES = ['founder', 'ceo', 'super_admin', 'admin', 'finance_director'];

const RoleGate = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const role = (user?.role || '').toLowerCase();
  if (allowedRoles.includes(role)) return children;
  return <Navigate to="/dashboard" replace />;
};

// Role groups for Step 3 access matrix (rbac_region_lockdown_step3_04may2026)
const GROUP_A_ADMIN_REP = [
  'founder', 'super_admin', 'admin_manager', 'admin', 'sales_rep', 'agent',
];
const GROUP_B_ADMIN_REP_FINANCE = [
  'founder', 'super_admin', 'admin_manager', 'admin', 'sales_rep', 'agent',
  'finance_officer',
];
// Group C is owner-self-only — empty allowedRoles + allowOwnerSelf
const GROUP_C_OWNER_ONLY = [];

const DashboardHome = () => {
  const { user, token } = useAuth();
  // ceo_instruction_ref: REGION-ARCHITECTURE-PHASE1-REFACTOR-01JUL2026
  // Load active regions once so the distributor-dashboard router
  // can dispatch on region_registry.features.my_products rather
  // than hardcoded "kenya".
  const [activeRegions, setActiveRegions] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/finance-director/regions/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setActiveRegions(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setActiveRegions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [backendUrl, token]);
  if (!user) return null;
  if (user?.must_change_password) return <StaffOnboarding />;
  // details_gate_27oct2026 (CEO) — HARD gate for ANY account with missing
  // details, every role. No 'Not now', no skip.
  if (user?.profile_incomplete) return <CompleteProfilePage />;

  // Demo users: route based on demo_view_as
  if (user?.is_demo || user?.role === 'demo_user') {
    const hasSeenStart = sessionStorage.getItem('rvk_demo_seen_dashboard');
    const viewAs = user?.demo_view_as || 'founder';
    // Salon demo goes straight to salon dashboard (StartHere is CEO-oriented)
    if (viewAs === 'salon' || viewAs === 'visitor') return <GlobalSalonDashboard />;
    if (!hasSeenStart) {
      sessionStorage.setItem('rvk_demo_seen_dashboard', 'true');
      return <StartHerePage />;
    }
    if (viewAs === 'distributor') return <UKDistributorDashboard />;
    if (viewAs === 'sales_rep') return <SalesRepDashboard />;
    return <AdminDashboard />;
  }
  const role = user?.role || 'salon';
  if (role === 'educator') return <EducatorHome />;
  const isAdmin = ['founder', 'admin', 'super_admin', 'admin_manager', 'warehouse_admin'].includes(role);
  if (isAdmin) return <AdminDashboard />;
  if (role === 'finance_director') return <NickyFinanceDashboard />;
  if (role === 'sales_rep') {
    // items 13+16 (Jun 2026): the old rep home is retired — Goodness lands on
    // the new-look rep platform. The old page stays reachable only in code.
    if (user?.is_super_rep) return <Navigate to="/dashboard/x/rep_platform_new_look" replace />;
    return <SalesRepDashboard />;
  }
  if (role === 'distributor') {
    const region = user?.region || 'nigeria';
    // ceo_instruction_ref: REGION-ARCHITECTURE-PHASE1-REFACTOR-01JUL2026
    // UK remains the primary distributor dashboard. Any other
    // region whose region_registry row has features.my_products
    // true also routes to UKDistributorDashboard.
    const regionHasMyProducts = Array.isArray(activeRegions)
      && activeRegions.some((r) => (r?.code || '').toLowerCase() === region
                                  && r?.features?.my_products === true);
    return (region === 'uk' || regionHasMyProducts)
      ? <UKDistributorDashboard />
      : <DistributorDashboard />;
  }
  if (role === 'delivery_agent') return <DeliveryDashboard />;
  if (role === 'agent') return <AgentDashboard />;
  if (role === 'team_member') return <TeamMemberDashboard />;
  if (role === 'salon_staff' && !['owner', 'manager'].includes((user?.salon_role || '').toLowerCase())) return <TeamMemberDashboard />;
  if (['salon', 'salon_owner', 'salon_manager', 'salon_business'].includes(role)) return <SalonHome27oct />;
  if (['salon_staff', 'public_customer'].includes(role)) return <GlobalSalonDashboard />;
  return <GlobalSalonDashboard />;
};

function BookAppRedirect() {
  /* platform_menu_reskin_01nov2026 — booking lives inside the platform shell */
  return <Navigate to="/dashboard/booking/home" replace />;
}

function AppRouter() {
  const location = useLocation();

  // academy_exit_button_21oct2026 — remember the last main-platform page
  // so the Academy "Back to platform" button can return the user there.
  useEffect(() => {
    const p = location.pathname;
    if (!p.startsWith('/academy') && p !== '/login' && p !== '/') {
      sessionStorage.setItem('rk_pre_academy_path', p + location.search);
    }
  }, [location]);

  // CRITICAL: Detect session_id in hash during render (not in useEffect)
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<div style={{minHeight:"40vh",display:"grid",placeItems:"center",fontFamily:"Georgia,serif",color:"#64525D"}}>Loading…</div>}>
    <Routes>
      <Route path="/" element={<RootEntry />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/shop/nigeria" element={<ClientShopNG />} />
      {/* ceo_instruction_ref: universal_entry_country_landing_build_19aug2026 —
          per-country full landing pages (12 April locked spec) */}
      <Route path="/uk" element={<CountryLandingPage country="uk" />} />
      <Route path="/nigeria" element={<CountryLandingPage country="nigeria" />} />
      <Route path="/ghana" element={<CountryLandingPage country="ghana" />} />
      <Route path="/international" element={<CountryLandingPage country="international" />} />
      <Route path="/find-salon" element={<FindSalonPage />} />
      <Route path="/salon-directory" element={<SalonDirectory />} />
      <Route path="/salon/:userId" element={
        <ProtectedRoute>
          <SalonIntelGate
            allowedRoles={GROUP_B_ADMIN_REP_FINANCE}
            allowOwnerSelf
            ownerIdParam="userId"
          >
            <div className="notranslate" translate="no" style={{ display: 'contents' }}>
              <SalonProfilePage />
            </div>
          </SalonIntelGate>
        </ProtectedRoute>
      } />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/academy" element={
        <ProtectedRoute>
          <AcademyLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AcademyHome />} />
        <Route path="welcome" element={<AcademyWelcome />} />
        <Route path="hair-textures" element={<AcademyHairTextures />} />
        <Route path="courses" element={<AcademyCourses />} />
        <Route path="products" element={<AcademyProducts />} />
        <Route path="product" element={<AcademyProduct />} />
        <Route path="sales" element={<AcademySales />} />
        <Route path="setup" element={<AcademySetup />} />
        <Route path="preview" element={<AcademyPreview />} />
        <Route path="lesson" element={<AcademyLesson />} />
        <Route path="test" element={<AcademyTest />} />
        <Route path="certificate" element={<AcademyCertificate />} />
        <Route path="live" element={<AcademyLive />} />
        <Route path="finder" element={<AcademyFinder />} />
        <Route path="compare" element={<AcademyCompare />} />
        <Route path="my-learning" element={<AcademyMyLearning />} />
        <Route path="classes" element={<ClassManage />} />
        <Route path="class/:classId" element={<ClassRoom />} />
        <Route path="class/:classId/present" element={<ClassPresent />} />
        <Route path="class/:classId/console" element={<ClassConsole />} />
        <Route path="training" element={<AcademyEducation />} />
        <Route path="method" element={<AcademyMethod />} />
        <Route path="rankings" element={<AcademyRankings />} />
        <Route path="ceo" element={<AcademyCeo />} />
        <Route path="questions" element={<AcademyQuestionBank />} />
        <Route path="journal" element={<JournalPage />} />
        <Route path="journal/story/:slug" element={<JournalStoryPage />} />
        <Route path="journal/editor" element={<JournalEditor />} />
        <Route path="science" element={<AcademyScience />} />
        <Route path="kprotein" element={<AcademyRefPage pageKey="kprotein" />} />
        <Route path="scalp-spray" element={<AcademyRefPage pageKey="scalp" />} />
        <Route path="satin-leave-in" element={<AcademyRefPage pageKey="satin" />} />
        <Route path="notes" element={<AcademyNotes />} />
        <Route path="ideas" element={<AcademyIdeas />} />
        <Route path="insights" element={<AcademyInsights />} />
      </Route>
      <Route path="/booking" element={<BookAppRedirect />}>
        <Route index element={<BookingHome />} />
        <Route path="calendar" element={<BookingCalendar />} />
        <Route path="clients" element={<BookingClients />} />
        <Route path="checkout" element={<BookingCheckout />} />
        <Route path="team" element={<BookingTeam />} />
        <Route path="stock" element={<BookingStock />} />
        <Route path="scan" element={<BookingScan />} />
        <Route path="jtrain" element={<BookingJuniorTraining />} />
        <Route path="plans" element={<BookingPlans />} />
        <Route path="ai" element={<BookingAI />} />
        <Route path="reports" element={<BookingReports />} />
        <Route path="settings" element={<BookingSettings />} />
        <Route path="network" element={<BookingNetwork />} />
        <Route path="help" element={<BookingHelp />} />
        <Route path="services" element={<BookingServicesAdmin />} />
        <Route path="noticeboard" element={<BookingNoticeboard />} />
        <Route path="profile" element={<BookingProfile />} />
        <Route path="online" element={<BookingOnlineLinks />} />
        <Route path="forms" element={<BookingFormsAdmin />} />
        <Route path="payments" element={<BkPaymentsPage />} />
        <Route path="reviews" element={<BookingReviewsPoints />} />
        <Route path="vouchers" element={<BookingVouchers />} />
        <Route path="waitlist" element={<BookingWaitlist />} />
        <Route path="juniors" element={<BookingJuniors />} />
        <Route path="education" element={<BookingEducation />} />
        <Route path="usewaste" element={<BookingUseWaste />} />
        <Route path="reorder" element={<BookingReorder />} />
        <Route path="marketing" element={<BookingMarketing />} />
      </Route>
      <Route path="/register/:slug" element={<RegisterFormPage />} />
      <Route path="/booking/pay/success" element={<BkPayResult />} />
      <Route path="/booking/pay/cancel" element={<BkPayResult />} />
      <Route path="/salon-finder" element={<SalonFinderPage />} />
      <Route path="/journal" element={<JournalPage />} />
      <Route path="/journal/story/:slug" element={<JournalStoryPage />} />
      <Route path="/bymarina" element={<ByMarinaShopPage />} />
      <Route path="/booking-site/:slug" element={<BookOnlinePage />} />
      <Route path="/door/:slug" element={<DoorCheckInPage />} />
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
      </Route>
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/book/:lookId" element={<LeadCapture />} />
      <Route path="/demo/join/:token" element={<DemoLandingPage />} />
      <Route path="/welcome/:token" element={<WelcomePage />} />
      <Route path="/salon/join/:token" element={<SalonJoinPage />} />
      <Route path="/distributor/join/:token" element={<DistributorJoinPage />} />
      <Route path="/share/:token" element={<ShareViewPage />} />
      <Route path="/quote-accept/:quote_id" element={<QuoteAcceptPage />} />
      <Route path="/quote-accept/:quote_id/changes" element={<QuoteChangeRequestPage />} />
      <Route path="/explore" element={<VisitorDemoPage />} />
      <Route path="/set-password/:token" element={<SetPasswordPage />} />
      <Route path="/finish-product" element={
        <ProtectedRoute>
          <div className="notranslate" translate="no" style={{ display: 'contents' }}>
            <FinishProduct />
          </div>
        </ProtectedRoute>
      } />
      {/* item 33 (Jun 2026): the new right-platform registration replaces the
          old form; the old one stays reachable at /register/old */}
      <Route path="/register" element={<PublicCatchupEmbed page="register" />} />
      <Route path="/register/old" element={<RegisterPage />} />
      <Route path="/register/salon" element={<UKSalonRegisterPage />} />
      <Route path="/staff-invite/:token" element={<StaffInviteAcceptPage />} />
      <Route path="/join/:code" element={<JoinClass />} />
      <Route path="/verify" element={<VerifyCertificate />} />
      <Route path="/join" element={<PublicRegisterPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <PendingGate>
            <RegionGate>
              <DashboardLayout />
            </RegionGate>
          </PendingGate>
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="booking/:view" element={<BookingEmbedPage />} />
        <Route path="x/:page" element={<CatchupEmbedPage />} />
        <Route path="catalogue" element={<ProductRangePage />} />
        <Route path="catalogue/keratin-kits" element={<KeratinKitsPage />} />
        <Route path="catalogue/classic" element={<ProductCatalogue />} />
        <Route path="catalogue/:productId" element={<ProductDetailPage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="edu-calendar" element={<EducatorCalendar />} />
        <Route path="edu-live" element={<EducatorLive />} />
        <Route path="edu-ask" element={<EducatorAsk />} />
        <Route path="edu-requests" element={<EducatorRequests />} />
        <Route path="edu-students" element={<EducatorStudents />} />
        <Route path="rep-quick" element={<RepQuickOrder />} />
        <Route path="rep-salons" element={<RepSalons />} />
        <Route path="rep-suite-orders" element={<RepOrders />} />
        <Route path="rep-leads" element={<RepLeads />} />
        <Route path="rep-commission" element={<RepCommission />} />
        <Route path="agent-clients" element={<AgentClients />} />
        <Route path="agent-new-order" element={<AgentNewOrder />} />
        <Route path="agent-suite-commission" element={<AgentCommission />} />
        <Route path="agent-suite-orders" element={<AgentOrders />} />
        <Route path="dist-place-order" element={<DistPlaceOrder />} />
        <Route path="dist-orders" element={<DistOrders />} />
        <Route path="dist-invoices" element={<DistInvoices />} />
        <Route path="dist-salons" element={<DistSalons />} />
        <Route path="ng-dispatch-history" element={<NigeriaDispatchHistoryPage />} />
        <Route path="calculator" element={<ProfitCalculator />} />
        <Route path="starter-packs" element={<StarterPacksPage />} />
                <Route path="starter-packs/:packId" element={<StarterPackDetailPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="finance-director" element={<RoleGate allowedRoles={FINANCE_DIRECTOR_ROLES}><NickyFinanceDashboard /></RoleGate>} />
        <Route path="distributor-packs" element={<DistributorPacksPage />} />
        <Route path="distributor-packs/:tierId" element={<DistributorPackDetailPage />} />
        {/* ceo_instruction_ref: kenya_my_products_nav_fix_30jun2026 */}
        <Route path="my-products" element={<KenyaMyProductsPage />} />
        {/* ceo_instruction_ref: kenya_factory_direct_build3_24jul2026 */}
        <Route path="kenya-orders" element={<KenyaOrderTracker />} />
        {/* ceo_instruction_ref: CAMPAIGN-IMAGES-30JUN2026-STAGE1 */}
        <Route path="campaign-images" element={<CampaignImagesPage />} />
        <Route path="my-team" element={<MyTeamPage />} />
        <Route path="salon-teams" element={<CEOTeamOverview />} />
        <Route path="discount-control" element={<CEODiscountControl />} />
        <Route path="visits" element={<VisitTrackingPage />} />
        <Route path="training" element={<Navigate to="/dashboard/learning" replace />} />
        <Route path="gallery" element={<GalleryPage />} />
        <Route path="rewards" element={<RewardsPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="revenue" element={<RevenueTracker />} />
        <Route path="pro-status" element={<ProStatusLadder />} />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="protocols" element={<Navigate to="/dashboard/learning" replace />} />
        <Route path="protocols/:protocolId" element={<Navigate to="/dashboard/learning" replace />} />
        <Route path="business-planner" element={<SalonBusinessPlanner />} />
        <Route path="reorder" element={<ReorderSuggestions />} />
        <Route path="security" element={<SecuritySettings />} />
        <Route path="thank-you" element={<ThankYouAdmin />} />
        <Route path="inventory" element={<UKWarehouse />} />
        <Route path="crm" element={<SalonIntelGate allowedRoles={GROUP_A_ADMIN_REP}><ProspectCRM /></SalonIntelGate>} />
        <Route path="forecast" element={<DemandForecast />} />
        <Route path="territory-performance" element={<TerritoryPerformance />} />
        <Route path="warehouse-reorder" element={<WarehouseReorder />} />
        <Route path="messages" element={<MessagingSystem standalone />} />
        <Route path="whatsapp" element={<WhatsAppBriefing />} />
        <Route path="verification" element={<VerificationQueue />} />
        <Route path="subscriptions" element={<SubscriptionPage />} />
        <Route path="verification-hub" element={<VerificationHub />} />
        <Route path="payroll" element={<PayrollDashboard />} />
        <Route path="my-payroll" element={<MyPayroll />} />
        <Route path="weekly-digest" element={<WeeklyDigest />} />
        <Route path="sales-funnel" element={<SalonIntelGate><SalesFunnel /></SalonIntelGate>} />
        <Route path="stock" element={<StockManagement />} />
        <Route path="stock/reconciliation" element={<StockReconciliation />} />
        <Route path="stock/scanner" element={<BarcodeScanner />} />
        <Route path="barcode" element={<BarcodeScanner />} />
        <Route path="product-scanner" element={<ProductScannerPage />} />
        <Route path="salon-settings" element={<SalonSettings />} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="salon-registry" element={<SalonIntelGate><SalonRegistryPage /></SalonIntelGate>} />
        <Route path="salon-performance" element={<SalonPerformanceDashboard />} />
        <Route path="ceo-command" element={<CEOCommandPage />} />
        <Route path="welcome-campaign" element={<RoleGate allowedRoles={['founder']}><WelcomeCampaignPage /></RoleGate>} />
        <Route path="run-migration" element={<RoleGate allowedRoles={['founder']}><RunMigrationPage /></RoleGate>} />
        <Route path="ceo-email-log" element={<CEOEmailLogPage />} />
        <Route path="faq" element={<FAQPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="lookbook" element={<Lookbook />} />
        <Route path="media-editor/:lookId" element={<MediaEditor />} />
        <Route path="moderation" element={<ModerationDashboard />} />
                <Route path="media-review" element={<MediaReviewPage />} />
        <Route path="sales-analytics" element={<SalesAnalytics />} />
        <Route path="rep/:userId" element={<RepDetail />} />
        <Route path="staff" element={<StaffDirectory />} />
        <Route path="share-pack" element={<RepSharePack />} />
        <Route path="leads" element={<SalonIntelGate><LeadsDashboard /></SalonIntelGate>} />
        <Route path="delivery" element={<DeliveryDashboard />} />
        <Route path="finish-product" element={<FinishProduct />} />
        <Route path="inventory-intelligence" element={<InventoryIntelligence />} />
        <Route path="feature-toggles" element={<FeatureToggles />} />
        <Route path="bulletins" element={<BulletinBoard />} />
                <Route path="rep-alerts" element={<RepAlerts />} />
                <Route path="stock-setup" element={<StockSetup />} />
                <Route path="salon-directory" element={<SalonIntelGate><SalonDirectory /></SalonIntelGate>} />
                <Route path="salon-profile/:userId" element={<SalonIntelGate allowedRoles={GROUP_B_ADMIN_REP_FINANCE} allowOwnerSelf ownerIdParam="userId"><DashboardSalonProfile /></SalonIntelGate>} />
                <Route path="editor/homepage" element={<HomepageEditor />} />
                <Route path="uk-warehouse" element={<UKWarehouse />} />
                <Route path="uk-dispatch" element={<UKDispatchDashboard />} />
                <Route path="quick-order" element={<QuickOrderPage />} />
                <Route path="create-order" element={<ReplacementCreatePage />} />
                <Route path="factory-orders" element={<FactoryOrders />} />
                <Route path="cost-dashboard" element={<CostDashboard />} />
                <Route path="finance" element={<FinanceDashboard />} />
                {/* ceo_instruction_ref: dead_finance_routes_redirect_16aug2026 —
                    legacy sub-routes' endpoints deleted 28 Jun 2026; redirect to live dashboard */}
                <Route path="finance-director/cashflow" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/invoices" element={<Navigate to="/dashboard/finance-director?tab=Invoices" replace />} />
                <Route path="finance-director/receipts" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/chasing" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/suppliers" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/expenses" element={<Navigate to="/dashboard/finance-director?tab=Expenses" replace />} />
                <Route path="finance-director/bank-feed" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/hair305" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/petty-cash" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/approvals" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="finance-director/audit-log" element={<Navigate to="/dashboard/finance-director" replace />} />
                <Route path="uk-distributors" element={<UKDistributors />} />
                <Route path="uk-orders" element={<RoleGate allowedRoles={UK_COMMERCIAL_ROLES}><UKOrdersPage /></RoleGate>} />
                <Route path="uk-orders/:orderId/dispatch" element={<RoleGate allowedRoles={UK_COMMERCIAL_ROLES}><UKOrderDispatchPage /></RoleGate>} />
                <Route path="reported-issues" element={<ReportedIssuesPage />} />
                <Route path="order-queue" element={<OrderQueuePage />} />
                <Route path="warehouse" element={<WarehouseDashboard />} />
                <Route path="dispatch-history" element={<DispatchHistoryPage />} />
                <Route path="distributor" element={<DistributorDashboard />} />
                <Route path="sales-log" element={<SalesLogPage />} />
                <Route path="invoices" element={<InvoicesRedirect />} />
                <Route path="uk-invoices" element={<RoleGate allowedRoles={UK_COMMERCIAL_ROLES}><UKInvoicesPage /></RoleGate>} />
                <Route path="ng-invoices" element={<NigeriaInvoicesPage />} />
                <Route path="my-invoices" element={<MySalesRepInvoicesPage />} />
                <Route path="quotes" element={<UKQuotesPage />} />
                <Route path="onboarding" element={<DistributorOnboardingPage />} />
                <Route path="salon-onboarding" element={<SalonOnboardingPage />} />
                <Route path="ng-salon-queue" element={<NigeriaSalonQueuePage />} />
                <Route path="onboard" element={<UnifiedOnboardingPage />} />
        <Route path="agent-commissions" element={<AgentCommissionsPage />} />
                <Route path="master" element={<MasterDashboardPage />} />
                <Route path="demo-invites" element={<SalonIntelGate><DemoInvitePage /></SalonIntelGate>} />
                <Route path="start" element={<StartHerePage />} />
                <Route path="salon-pipeline" element={<SalonIntelGate><SalonPipelinePage /></SalonIntelGate>} />
                <Route path="ng-payment-accounts" element={<NgPaymentAccountsPage />} />
                <Route path="ng-usd-pricing" element={<NgUsdPricingPage />} />
                <Route path="salon-pipeline/:salonId" element={<SalonIntelGate><SalonCRMDetailPage /></SalonIntelGate>} />
                <Route path="salon-preview/:salonId" element={<SalonIntelGate allowedRoles={GROUP_A_ADMIN_REP}><SalonPreviewPage /></SalonIntelGate>} />
                <Route path="stock-transfers" element={<StockTransferPage />} />
                <Route path="uk-salons" element={<SalonIntelGate allowedRoles={GROUP_A_ADMIN_REP}><UKSalonsAdminPage /></SalonIntelGate>} />
                <Route path="automation" element={<AutomationDashboard />} />
                <Route path="ceo-digest" element={<CEODigestPage />} />
                <Route path="whatsapp-automation" element={<WhatsAppAutomationPage />} />
                <Route path="whatsapp-inbox" element={<WhatsAppInbox />} />
                <Route path="education" element={<Navigate to="/dashboard/learning" replace />} />
                <Route path="training-module" element={<Navigate to="/dashboard/learning" replace />} />
                <Route path="learning" element={<LearningPage />} />
                <Route path="results-logbook" element={<ResultLogbookPage />} />
                <Route path="results-gallery" element={<ResultsGalleryPage />} />
                <Route path="before-after" element={<Navigate to="/dashboard/lookbook" replace />} />
                <Route path="reorder-intelligence" element={<ReorderIntelligencePage />} />
                <Route path="command-centre" element={<CommandCentrePage />} />
                <Route path="marketing/newsletter" element={<NewsletterGenerator />} />
                <Route path="marketing/newsletter/:draftId" element={<NewsletterGenerator />} />
                <Route path="payment-review" element={<PaymentReviewDashboard />} />
                <Route path="payment-review/:receiptId" element={<PaymentReviewDashboard />} />
                <Route path="my-receipts" element={<MyReceiptsPage />} />
                <Route path="help/receipt-guide" element={<ReceiptUploadGuide />} />
                <Route path="results/:entryId" element={<ResultDetailPage />} />
                <Route path="salon-search" element={<SalonIntelGate allowedRoles={GROUP_A_ADMIN_REP}><SalonSearchPage /></SalonIntelGate>} />
                <Route path="my-salon-orders" element={<SalonIntelGate allowedRoles={['distributor']} allowOwnerSelf><MySalonOrdersPage /></SalonIntelGate>} />
                <Route path="complete-details" element={<CompleteProfilePage />} />
                <Route path="my-salon-invoices" element={<SalonIntelGate allowedRoles={['distributor']} allowOwnerSelf><MySalonInvoicesPage /></SalonIntelGate>} />
                <Route path="my-salon-quotes" element={<SalonIntelGate allowedRoles={['distributor']} allowOwnerSelf><MySalonQuotesPage /></SalonIntelGate>} />
                <Route path="stock-check" element={<ManualStockCheckPage />} />
                <Route path="transfer-requests" element={<TransferRequestsPage />} />
                <Route path="manual-stock-check" element={<ManualStockCheckPage />} />
                <Route path="product-control" element={<ProductControlPage />} />
                <Route path="campaign-control" element={<CampaignControlPage />} />
                <Route path="stock-check-rep" element={<RepStockCheckPage />} />
                <Route path="stock-reps" element={<CEOStockRepsPage />} />
                <Route path="product-requests" element={<ProductRequestsPage />} />
                <Route path="security-log" element={<SecurityLogPage />} />
                <Route path="warehouse-overview" element={<WarehouseDashboard />} />
                <Route path="team-performance" element={<TeamPerformancePage />} />
                <Route path="super-rep" element={<SuperRepDashboard />} />
      </Route>
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <DemoBanner />
          <DemoConversionBanner />
          <AppRouter />
          <GuidedTour />
          <Toaster position="top-right" duration={20000} closeButton />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
